import type { DisplayProduct, Industry, Product } from '../types';

export type ViewMode = 'category' | 'individual';

export const DEFAULT_IMAGE =
  'https://cdn-fastly.parrotanalytics.com/images/placeholders/decision-intelligence.png';

/** Base product code, dropping any `:entity` suffix used in industry priority lists. */
function baseCode(code: string): string {
  return code.split(':')[0] ?? code;
}

/**
 * Flattens API products into renderable cards. In "individual" view an
 * entity-scoped product expands into one card per entity; otherwise it's one
 * card per product.
 */
export function toDisplayProducts(products: Product[], viewMode: ViewMode): DisplayProduct[] {
  return products.flatMap((p) => {
    const media = p.media_assets ?? {};
    const shared = {
      description: p.description ?? '',
      image: media.image ?? DEFAULT_IMAGE,
      video: media.video,
      productCode: p.code,
      isAddon: p.is_addon,
      licensed: p.licensed ?? true,
      recommended: p.recommended ?? false,
      recommendationOrder: p.recommendation_order ?? null,
      ssoEnabled: p.sso_enabled ?? false,
    };

    // Individual view: each entity becomes its own card, so the entity is in the
    // name — no chips.
    if (viewMode === 'individual' && p.entity_scope && p.entity_scope.length > 0) {
      return p.entity_scope.map((entity) => ({
        ...shared,
        code: `${p.code}:${entity.toLowerCase()}`,
        displayName: `${entity} ${p.display_name}`,
        href: p.entity_urls?.[entity] ?? p.base_url ?? '',
        entityScope: [],
      }));
    }

    // One card per product; surface its entity types as chips.
    return [
      {
        ...shared,
        code: p.code,
        displayName: p.display_name,
        href: p.base_url ?? '',
        entityScope: p.entity_scope ?? [],
      },
    ];
  });
}

export function filterBySearch(products: DisplayProduct[], search: string): DisplayProduct[] {
  const term = search.trim().toLowerCase();
  if (!term) return products;
  return products.filter(
    (p) => p.displayName.toLowerCase().includes(term) || p.description.toLowerCase().includes(term),
  );
}

/**
 * Admin industry filter. An industry's `products` list is the source of truth for
 * membership and priority order; products are filtered to that list (matched on
 * base code) and sorted by their position in it.
 */
export function filterProductsByIndustry(
  products: Product[],
  industry: Industry | undefined,
): Product[] {
  if (!industry || industry.products.length === 0) return products;

  const priority = industry.products;
  const members = new Set(priority.map(baseCode));
  // A product's rank = the first position its base code appears in the priority list.
  const rankOf = (code: string) => {
    const i = priority.findIndex((entry) => baseCode(entry) === code);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };

  return products
    .filter((p) => members.has(p.code))
    .toSorted((a, b) => rankOf(a.code) - rankOf(b.code));
}

export interface ProductBuckets {
  /** Products the viewer is licensed for. */
  licensed: DisplayProduct[];
  /** Unlicensed, but server-flagged as recommended for the viewer. */
  recommended: DisplayProduct[];
  /** Everything else. */
  other: DisplayProduct[];
}

/**
 * Splits products into the customer-view swimlanes using the server-computed
 * `recommended` flag. Licensed + recommended lanes are ordered by
 * `recommendationOrder`.
 */
export function bucketProducts(products: DisplayProduct[]): ProductBuckets {
  const byOrder = (a: DisplayProduct, b: DisplayProduct) =>
    (a.recommendationOrder ?? Number.POSITIVE_INFINITY) -
    (b.recommendationOrder ?? Number.POSITIVE_INFINITY);

  return {
    licensed: products.filter((p) => p.licensed).toSorted(byOrder),
    recommended: products.filter((p) => !p.licensed && p.recommended).toSorted(byOrder),
    other: products.filter((p) => !p.licensed && !p.recommended),
  };
}
